import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import env from '@utils/env'
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  MessageCircle,
  User,
  Building,
  BookOpen,
  CheckCircle
} from 'lucide-react'
import { contactsAPI, settingsAPI } from '@utils/api'
import toast from 'react-hot-toast'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

const OfficeMap = ({ offices }) => {
  const mapRef = useRef(null)
  const mapInstance = useRef(null)

  useEffect(() => {
    if (!mapRef.current || offices.length === 0) return
    if (mapInstance.current) {
      mapInstance.current.remove()
      mapInstance.current = null
    }
    const validOffices = offices.filter(o => o.lat && o.lng)
    if (validOffices.length === 0) return

    const center = validOffices[0].isHeadquarters
      ? [validOffices[0].lat, validOffices[0].lng]
      : [validOffices[0].lat, validOffices[0].lng]

    const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false }).setView(center, 13)
    mapInstance.current = map

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map)

    const hqIcon = L.divIcon({
      html: `<div style="background:#6d28d9;color:white;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><span style="transform:rotate(45deg);font-size:14px;">★</span></div>`,
      className: '', iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -36],
    })
    const branchIcon = L.divIcon({
      html: `<div style="background:#2563eb;color:white;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>`,
      className: '', iconSize: [28, 28], iconAnchor: [14, 28], popupAnchor: [0, -32],
    })

    validOffices.forEach(office => {
      const marker = L.marker([Number(office.lat), Number(office.lng)], {
        icon: office.isHeadquarters ? hqIcon : branchIcon
      }).addTo(map)
      marker.bindPopup(`
        <div style="min-width:180px;font-family:sans-serif;">
          <strong style="font-size:14px;">${office.name}${office.isHeadquarters ? ' ⭐' : ''}</strong><br/>
          <span style="color:#6b7280;font-size:12px;">${office.address}, ${office.city}, ${office.country}</span><br/>
          ${office.phone ? `<span style="font-size:12px;">📞 ${office.phone}</span><br/>` : ''}
          ${office.email ? `<span style="font-size:12px;">✉ ${office.email}</span>` : ''}
        </div>
      `)
    })

    if (validOffices.length > 1) {
      const bounds = L.latLngBounds(validOffices.map(o => [Number(o.lat), Number(o.lng)]))
      map.fitBounds(bounds, { padding: [40, 40] })
    }

    return () => { map.remove(); mapInstance.current = null }
  }, [offices])

  return (
    <div ref={mapRef} className="w-full h-80 rounded-2xl overflow-hidden z-0" />
  )
}

const Contact = () => {
  const [siteSettings, setSiteSettings] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    courseInterest: [],
    experience: '',
    education: '',
    message: '',
    gdprConsent: false
  })
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    settingsAPI.getPublic().then(res => setSiteSettings(res.data)).catch(() => {})
  }, [])

  const phones = (siteSettings?.supportPhones?.length ? siteSettings.supportPhones : null)
    || (siteSettings?.supportPhone ? [siteSettings.supportPhone] : null)
    || [`+${env.whatsappNumber}`]
  const phone = phones[0]
  const email = siteSettings?.supportEmail || env.supportEmail
  const whatsapp = siteSettings?.whatsappNumber || env.whatsappNumber
  const hours = siteSettings?.workingHours || 'Mon - Sat: 9:00 AM - 7:00 PM'
  const offices = siteSettings?.offices || []

  const experiences = ['Fresher', '0-1 years', '1-3 years', '3-5 years', '5+ years']
  const educationOptions = ['High School', 'Diploma', "Bachelor's", "Master's", 'PhD', 'Other']
  
  const popularCourses = [
    'Linux & Networking Essentials',
    'Cloud Essentials',
    'DevOps Essentials',
    'Kubernetes Architect',
    'AWS Professional Architect',
    'GCP Professional Architect',
    'Azure Architect'
  ]

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleCourseToggle = (course) => {
    setFormData(prev => ({
      ...prev,
      courseInterest: prev.courseInterest.includes(course)
        ? prev.courseInterest.filter(c => c !== course)
        : [...prev.courseInterest, course]
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.gdprConsent) {
      toast.error('Please accept the privacy policy to proceed')
      return
    }

    setLoading(true)
    try {
      await contactsAPI.submit({ ...formData, source: 'contact_page' })
      setSubmitted(true)
      toast.success('Your inquiry has been submitted successfully!')
    } catch (error) {
      toast.error(error.message || 'Failed to submit inquiry')
    }
    setLoading(false)
  }

  const contactInfo = [
    { icon: Phone, label: phones.length > 1 ? 'Phone Numbers' : 'Phone', value: phones.length > 1 ? phones.join(' / ') : phone, href: `tel:${phone}`, color: 'text-blue-600', bgColor: 'bg-blue-100' },
    { icon: Mail, label: 'Email', value: email, href: `mailto:${email}`, color: 'text-green-600', bgColor: 'bg-green-100' },
    { icon: MapPin, label: 'Office', value: offices[0] ? `${offices[0].city}, ${offices[0].country}` : 'Bangalore, India', href: '#', color: 'text-purple-600', bgColor: 'bg-purple-100' },
    { icon: Clock, label: 'Working Hours', value: hours, href: '#', color: 'text-orange-600', bgColor: 'bg-orange-100' },
  ]

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full mx-4"
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-large p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Thank You for Your Interest!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Our course advisor will contact you within 24 hours to discuss your learning journey.
            </p>
            <div className="space-y-3">
              <a
                href={`https://wa.me/${whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary w-full"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat on WhatsApp
              </a>
              <button
                onClick={() => setSubmitted(false)}
                className="btn btn-outline w-full"
              >
                Submit Another Inquiry
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  const fi = 'w-full px-4 py-3 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all'
  const lbl = 'block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1.5'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">

      {/* ── Hero ── */}
      <section className="relative bg-gradient-to-br from-primary-700 via-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-black/10 rounded-full blur-3xl" />
        </div>
        <div className="container-custom relative py-16 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 rounded-full text-xs font-semibold tracking-wide uppercase mb-4">
              <MessageCircle className="w-3.5 h-3.5" /> Get In Touch
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-4">
              Start Your DevOps Journey
            </h1>
            <p className="text-base sm:text-lg text-primary-100 max-w-xl">
              Talk to our expert counselors and find the perfect course to transform your career.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── 3-col contact cards ── */}
      <section className="container-custom -mt-6 relative z-10 mb-2">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {contactInfo.map((info, i) => (
            <motion.a
              key={i} href={info.href}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="group bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 sm:p-5 flex flex-col items-center text-center hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 ${info.bgColor} dark:bg-opacity-20 rounded-xl flex items-center justify-center mb-3`}>
                <info.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${info.color}`} />
              </div>
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">{info.label}</p>
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{info.value}</p>
            </motion.a>
          ))}
        </div>
      </section>

      {/* ── Main 2-col: sidebar + form ── */}
      <section className="container-custom py-10 lg:py-14">
        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 items-start">

          {/* ── Left sidebar (sticky on desktop) ── */}
          <motion.aside
            initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 lg:sticky lg:top-24 space-y-5"
          >
            {/* Intro card */}
            <div className="bg-gradient-to-br from-primary-600 to-accent-600 rounded-2xl p-6 text-white">
              <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <BookOpen className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-extrabold mb-2">Enroll &amp; Transform</h2>
              <p className="text-sm text-primary-100 leading-relaxed">
                Fill in the form and our advisor will reach out within 24 hours with a personalised plan.
              </p>
              <a
                href={`https://wa.me/${whatsapp}`}
                target="_blank" rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-white text-primary-700 font-semibold text-sm rounded-xl hover:bg-primary-50 transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
            </div>

            {/* Contact details */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-5 space-y-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Contact Details</h3>

              {/* All phone numbers */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Phone className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{phones.length > 1 ? 'Phone Numbers' : 'Phone'}</p>
                  <div className="space-y-0.5 mt-0.5">
                    {phones.map((p, i) => (
                      <a key={i} href={`tel:${p}`} className="block text-sm font-semibold text-gray-800 dark:text-gray-200 hover:text-primary-600 transition-colors">{p}</a>
                    ))}
                  </div>
                </div>
              </div>

              {[
                { icon: Mail,  label: 'Email', value: email, href: `mailto:${email}`, color: 'text-green-600',  bg: 'bg-green-50 dark:bg-green-900/20' },
                { icon: Clock, label: 'Hours', value: hours, href: '#',               color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20' },
              ].map((c, i) => (
                <a key={i} href={c.href} className="flex items-center gap-3 group">
                  <div className={`w-9 h-9 rounded-xl ${c.bg} flex items-center justify-center shrink-0`}>
                    <c.icon className={`w-4 h-4 ${c.color}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">{c.label}</p>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-primary-600 transition-colors">{c.value}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Office quick list */}
            {offices.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-5">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Our Offices</h3>
                <div className="space-y-3">
                  {offices.map((o, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center shrink-0 mt-0.5">
                        <MapPin className="w-3.5 h-3.5 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-800 dark:text-gray-200">
                          {o.name} {o.isHeadquarters && <span className="text-yellow-500">★</span>}
                        </p>
                        <p className="text-xs text-gray-500 leading-snug">{o.address}, {o.city}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.aside>

          {/* ── Right: form ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 sm:p-8"
          >
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white">Send Us a Message</h2>
              <p className="text-sm text-gray-500 mt-1">All fields marked * are required</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Row 1: name + email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                      required placeholder="John Doe" className={`${fi} pl-10`} />
                  </div>
                </div>
                <div>
                  <label className={lbl}>Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                      required placeholder="john@example.com" className={`${fi} pl-10`} />
                  </div>
                </div>
              </div>

              {/* Row 2: phone + country */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                      required placeholder="+91 98765 43210" className={`${fi} pl-10`} />
                  </div>
                </div>
                <div>
                  <label className={lbl}>Country *</label>
                  <div className="relative">
                    <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input type="text" name="country" value={formData.country} onChange={handleInputChange}
                      required placeholder="India" className={`${fi} pl-10`} />
                  </div>
                </div>
              </div>

              {/* Row 3: experience + education */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Work Experience *</label>
                  <select name="experience" value={formData.experience} onChange={handleInputChange} required className={fi}>
                    <option value="">Select experience</option>
                    {experiences.map(e => <option key={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Education *</label>
                  <select name="education" value={formData.education} onChange={handleInputChange} required className={fi}>
                    <option value="">Select education</option>
                    {educationOptions.map(e => <option key={e}>{e}</option>)}
                  </select>
                </div>
              </div>

              {/* Course interest — 3-col grid */}
              <div>
                <label className={lbl}>Courses Interested In</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                  {popularCourses.map(course => (
                    <label key={course}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border cursor-pointer text-sm transition-all
                        ${formData.courseInterest.includes(course)
                          ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                          : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary-300 dark:hover:border-primary-700 bg-gray-50 dark:bg-gray-800'
                        }`}
                    >
                      <input type="checkbox" checked={formData.courseInterest.includes(course)}
                        onChange={() => handleCourseToggle(course)} className="w-4 h-4 accent-primary-600 shrink-0" />
                      <span className="leading-snug text-xs font-medium">{course}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className={lbl}>Career Goals</label>
                <textarea name="message" value={formData.message} onChange={handleInputChange}
                  rows={4} placeholder="I want to become a DevOps engineer and..."
                  className={`${fi} resize-none`} />
              </div>

              {/* GDPR */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" name="gdprConsent" checked={formData.gdprConsent}
                  onChange={handleInputChange} required
                  className="w-4 h-4 mt-0.5 accent-primary-600 shrink-0" />
                <span className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  I agree to the processing of my personal data in accordance with the{' '}
                  <a href="/privacy" className="text-primary-600 hover:underline">Privacy Policy</a>
                </span>
              </label>

              {/* Submit */}
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold text-sm shadow-lg shadow-primary-600/25 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0 transition-all">
                {loading
                  ? <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Submitting…</>
                  : <><Send className="w-4 h-4" /> Submit Inquiry</>
                }
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* ── Office map ── */}
      {offices.length > 0 && (
        <section className="container-custom pb-14">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-extrabold text-gray-900 dark:text-white">Our Offices</h2>
                <p className="text-sm text-gray-500 mt-0.5">Click a pin for details</p>
              </div>
              <div className="flex gap-2">
                {offices.map((o, i) => (
                  <span key={i} className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-xl text-xs font-semibold text-gray-700 dark:text-gray-300">
                    <MapPin className="w-3 h-3 text-primary-600" />{o.city}
                    {o.isHeadquarters && <span className="text-yellow-500">★</span>}
                  </span>
                ))}
              </div>
            </div>
            {/* Office cards — 3 col */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100 dark:bg-gray-800">
              {offices.map((o, i) => (
                <div key={i} className="bg-white dark:bg-gray-900 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
                      <MapPin className="w-3.5 h-3.5 text-primary-600" />
                    </div>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{o.name}</span>
                    {o.isHeadquarters && <span className="text-xs px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full font-semibold">HQ</span>}
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">{o.address}, {o.city}, {o.country}</p>
                  {o.phone && <p className="text-xs text-gray-500 mt-1">📞 {o.phone}</p>}
                  {o.email && <p className="text-xs text-gray-500">✉ {o.email}</p>}
                </div>
              ))}
            </div>
            <div className="p-4">
              <OfficeMap offices={offices} />
            </div>
          </div>
        </section>
      )}

    </div>
  )
}

export default Contact
